import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileDown,
  Mail,
  Printer,
  StickyNote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { api, errText } from "@/lib/api";
import { downloadDueBoardPdf } from "@/lib/exportSheet";
import { useAuth } from "@/context/AuthContext";
import { addDays, isoDate, shortDate, startOfWeek, whenLabel } from "@/lib/eventMeta";

const Stat = ({ label, value, tone = "" }) => (
  <div className="border border-border rounded-sm px-4 py-3">
    <p className="label-caps">{label}</p>
    <p className={`text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

export default function DueBoard() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [anchor, setAnchor] = useState(() =>
    startOfWeek(params.get("start") ? new Date(`${params.get("start")}T00:00:00`) : new Date()),
  );
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const start = isoDate(startOfWeek(anchor));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/due-board", { params: { start } });
      setBoard(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, [start]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setParams({ start }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  const tick = async (todo) => {
    try {
      await api.post(`/todos/${todo.todo_id}/toggle`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const totals = board?.totals || {};
  const groups = board?.groups || [];
  const schedule = board?.schedule || [];

  return (
    <div className="space-y-8" data-testid="due-board-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">Due board</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
            Everything due
          </h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="due-board-range">
            {board ? `${shortDate(board.start)} — ${shortDate(board.end)}` : "…"}
            {totals.overdue ? ` · ${totals.overdue} overdue` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Button
            variant="outline"
            data-testid="due-prev-week"
            onClick={() => setAnchor(addDays(anchor, -7))}
            className="rounded-sm h-11 w-11 p-0"
            aria-label="The week before"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            data-testid="due-this-week"
            onClick={() => setAnchor(startOfWeek(new Date()))}
            className="rounded-sm h-11 px-4"
          >
            This week
          </Button>
          <Button
            variant="outline"
            data-testid="due-next-week"
            onClick={() => setAnchor(addDays(anchor, 7))}
            className="rounded-sm h-11 w-11 p-0"
            aria-label="The week after"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            data-testid="due-pdf-btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await downloadDueBoardPdf(start);
                toast.success("Due board saved as a PDF");
              } catch (e) {
                toast.error(errText(e));
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-sm h-11 px-4"
          >
            <FileDown className="h-4 w-4 mr-2" /> PDF
          </Button>
          <Button
            variant="outline"
            data-testid="due-email-btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await api.post("/due-board/email", { start });
                toast.success(`Emailed to ${data.sent_to.join(", ")}`);
              } catch (e) {
                toast.error(errText(e));
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-sm h-11 px-4"
          >
            <Mail className="h-4 w-4 mr-2" /> Email it
          </Button>
          <Button
            variant="outline"
            data-testid="due-print-btn"
            onClick={() => window.print()}
            className="rounded-sm h-11 px-4"
          >
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="due-board-totals">
        <Stat
          label="Overdue"
          value={totals.overdue || 0}
          tone={totals.overdue ? "text-destructive" : ""}
        />
        <Stat label="Falling due" value={totals.due_this_week || 0} />
        <Stat label="Listings" value={totals.listings || 0} />
        <Stat label="Matters" value={totals.matters || 0} />
      </div>

      {loading && !board ? (
        <p className="text-sm text-muted-foreground">Gathering the week…</p>
      ) : groups.length ? (
        <div className="space-y-6">
          {groups.map((g) => (
            <section
              key={g.matter_id || "none"}
              data-testid={`due-group-${g.matter_id || "none"}`}
              className="surface p-5 sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  {g.matter_id ? (
                    <button
                      data-testid={`due-matter-link-${g.matter_id}`}
                      onClick={() => navigate(`/matters/${g.matter_id}`)}
                      className="text-left font-heading text-xl font-bold tracking-tight hover:underline"
                    >
                      {g.matter_title}
                    </button>
                  ) : (
                    <p className="font-heading text-xl font-bold tracking-tight">{g.matter_title}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {[g.case_number, g.client].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.overdue ? (
                    <span
                      className="chip chip-fixed"
                      data-testid={`due-overdue-chip-${g.matter_id || "none"}`}
                    >
                      <AlertTriangle className="h-3 w-3" /> {g.overdue} overdue
                    </span>
                  ) : null}
                  {g.due_this_week ? (
                    <span className="chip chip-morning">{g.due_this_week} falling due</span>
                  ) : null}
                </div>
              </div>

              {g.todos.length ? (
                <div className="mt-5">
                  <p className="label-caps">Falling due</p>
                  <div className="mt-2 space-y-2">
                    {g.todos.map((t) => (
                      <div
                        key={t.todo_id}
                        data-testid={`due-todo-${t.todo_id}`}
                        className={`flex items-start gap-3 border border-border rounded-sm px-3 py-2.5 ${
                          t.done ? "opacity-60" : ""
                        }`}
                      >
                        <button
                          data-testid={`due-todo-toggle-${t.todo_id}`}
                          disabled={!canWrite}
                          onClick={() => tick(t)}
                          className={`mt-0.5 h-5 w-5 shrink-0 grid place-items-center border rounded-sm transition-colors duration-200 ${
                            t.done
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-input"
                          }`}
                          aria-label={t.done ? "Mark as still open" : "Tick it off"}
                        >
                          {t.done ? <Check className="h-3.5 w-3.5" /> : null}
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold ${t.done ? "line-through" : ""}`}>
                            {t.title}
                          </p>
                          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            {t.due_date ? (
                              <span
                                className={`font-semibold uppercase tracking-wider ${
                                  t.overdue ? "text-destructive" : ""
                                }`}
                              >
                                {t.overdue ? "Overdue · due " : "Due "}
                                {shortDate(t.due_date)}
                              </span>
                            ) : null}
                            {t.priority === "high" ? (
                              <span className="inline-flex items-center gap-1 text-destructive font-semibold">
                                <AlertTriangle className="h-3 w-3" /> High
                              </span>
                            ) : null}
                            {t.time_hint ? (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {t.time_hint}
                              </span>
                            ) : null}
                          </p>
                          <Markdown
                            text={t.notes}
                            className="mt-1.5 text-xs text-muted-foreground"
                          />
                        </div>
                        <button
                          data-testid={`due-todo-open-${t.todo_id}`}
                          onClick={() => navigate(`/todos?date=${t.date}`)}
                          className="text-xs font-semibold underline shrink-0 print:hidden"
                        >
                          On the list
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {g.postits.length ? (
                <div className="mt-5">
                  <p className="label-caps">Post-its lapsing</p>
                  <div className="mt-2 space-y-2">
                    {g.postits.map((n) => (
                      <button
                        key={n.post_it_id}
                        data-testid={`due-postit-${n.post_it_id}`}
                        onClick={() => navigate("/postits")}
                        className="w-full text-left flex items-start gap-3 border border-border rounded-sm px-3 py-2.5 hover:bg-accent/40 transition-colors duration-200"
                      >
                        <StickyNote className="h-4 w-4 mt-0.5 shrink-0 opacity-60" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold">
                            {n.title || "Post-it"}
                          </span>
                          {n.expires_on ? (
                            <span
                              className={`mt-1 block text-xs font-semibold uppercase tracking-wider ${
                                n.lapsed ? "text-destructive" : "text-muted-foreground"
                              }`}
                            >
                              {n.lapsed ? "Lapsed · " : "Lapses "}
                              {shortDate(n.expires_on)}
                            </span>
                          ) : null}
                          <Markdown
                            text={n.body}
                            className="mt-1.5 text-xs text-muted-foreground line-clamp-3"
                          />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ))}
        </div>
      ) : (
        <div className="surface p-10 text-center" data-testid="due-board-empty">
          <p className="text-base font-semibold">Nothing falls due this week</p>
          <p className="mt-1 text-sm text-muted-foreground">
            No deadlines and no lapsing post-its between {board ? shortDate(board.start) : ""} and{" "}
            {board ? shortDate(board.end) : ""}.
          </p>
        </div>
      )}

      {schedule.length ? (
        <section className="surface p-5 sm:p-6" data-testid="due-schedule">
          <p className="label-caps">In court this week</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">
            {schedule.length} matter{schedule.length === 1 ? "" : "s"} scheduled
          </h2>
          <div className="mt-4 divide-y divide-border">
            {schedule.map((row) => (
              <div
                key={row.matter_id || row.matter_title}
                data-testid={`due-scheduled-${row.matter_id || "unlinked"}`}
                className="py-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4"
              >
                <div className="min-w-0 sm:flex-1">
                  {row.matter_id ? (
                    <button
                      onClick={() => navigate(`/matters/${row.matter_id}`)}
                      className="text-left text-sm font-semibold hover:underline"
                    >
                      {row.matter_title}
                    </button>
                  ) : (
                    <p className="text-sm font-semibold">{row.matter_title}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {[row.case_number, ...(row.courts || [])].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:justify-end sm:w-[22rem]">
                  {row.days.map((d) => (
                    <button
                      key={d.event_id}
                      data-testid={`due-day-${d.event_id}`}
                      onClick={() =>
                        navigate(`/calendar?view=day&date=${d.date}&entry=${d.event_id}`)
                      }
                      className={`h-8 px-3 rounded-full border text-xs font-medium transition-colors duration-200 hover:bg-accent ${
                        d.clash ? "border-destructive text-destructive" : "border-border"
                      }`}
                      title={d.title}
                    >
                      {shortDate(d.date)} · {whenLabel(d)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
